import { Fragment, useMemo, useState } from "react";
import { Collapse, Table } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import {
  fetchActionsUtil,
  formatDate,
  formatForQty,
} from "../../../utils/helpers";
import currency from "currency.js";
import {
  addDays,
  addMonths,
  addQuarters,
  addYears,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfDay,
  format,
  getDate,
  getDaysInMonth,
  startOfDay,
} from "date-fns";
import { useQuery } from "react-query";
import { useAuth } from "../../../hooks/useAuth";
import queryString from "query-string";
import { ExcelIcon } from "../../Icons";
import NoTableItem from "../../utils/NoTableItem";
import CachedIcon from "mdi-react/CachedIcon";
import ChevronDownIcon from "mdi-react/ChevronDownIcon";
import ChevronRightIcon from "mdi-react/ChevronRightIcon";
import * as XLSX from "xlsx";

const EPSILON = 0.01;
const MAX_PROJECTION_ITERATIONS_FAILSAFE = 5000;

const getResponseBarCode = itemData =>
  itemData?.Bar_Code ?? itemData?.bar_code ?? itemData?.BarCode ?? "";

const normalizeItem = item => ({
  ...item,
  Bar_Code: item?.Bar_Code ?? item?.bar_code ?? item?.BarCode ?? "",
  Warehouse: item?.Warehouse ?? item?.W_ID ?? "",
  Item_Name: item?.Item_Name ?? item?.item_name ?? item?.ItemName ?? "",
  UnitCost: Number(item?.UnitCost ?? item?.Unit_Cost ?? item?.unitCost ?? 0),
  Quantity: Number(item?.Quantity ?? item?.Qty ?? item?.quantity ?? 0),
});

const getValidDate = value => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getProratedAmountForRange = ({
  amount,
  periodStart,
  periodEnd,
  rangeStart,
  rangeEnd,
}) => {
  const normalizedPeriodStart = startOfDay(periodStart);
  const normalizedPeriodEnd = endOfDay(periodEnd);
  const normalizedRangeStart = rangeStart ? startOfDay(rangeStart) : null;
  const normalizedRangeEnd = rangeEnd ? endOfDay(rangeEnd) : null;

  const shouldProrateStart =
    normalizedRangeStart && normalizedRangeStart > normalizedPeriodStart;
  const shouldProrateEnd =
    normalizedRangeEnd && normalizedRangeEnd < normalizedPeriodEnd;

  if (normalizedRangeStart && normalizedPeriodEnd < normalizedRangeStart) {
    return 0;
  }

  if (normalizedRangeEnd && normalizedPeriodStart > normalizedRangeEnd) {
    return 0;
  }

  const effectiveStart = normalizedRangeStart
    ? shouldProrateStart
      ? normalizedRangeStart > normalizedPeriodStart
        ? normalizedRangeStart
        : normalizedPeriodStart
      : normalizedPeriodStart
    : normalizedPeriodStart;

  const effectiveEnd = normalizedRangeEnd
    ? shouldProrateEnd
      ? normalizedRangeEnd < normalizedPeriodEnd
        ? normalizedRangeEnd
        : normalizedPeriodEnd
      : normalizedPeriodEnd
    : normalizedPeriodEnd;

  if (effectiveStart > effectiveEnd) {
    return 0;
  }

  const totalDays =
    differenceInCalendarDays(normalizedPeriodEnd, normalizedPeriodStart) + 1;
  const overlapDays =
    differenceInCalendarDays(effectiveEnd, effectiveStart) + 1;

  if (totalDays <= 0 || overlapDays <= 0) {
    return 0;
  }

  return currency(amount)
    .multiply(overlapDays)
    .divide(totalDays).value;
};

const expandPeriodToDailyRows = (periodProjections, openingValue) => {
  const dailyRows = [];
  let runningValue = openingValue;

  periodProjections.forEach(projection => {
    const pStart = startOfDay(projection.periodStart);
    const pEnd = startOfDay(projection.periodEnd);

    if (pStart > pEnd) return;

    const days = eachDayOfInterval({ start: pStart, end: pEnd });
    const totalDays = days.length;

    if (totalDays <= 0) return;

    const baseDailyAmount = currency(projection.amount).divide(totalDays).value;
    let allocated = 0;

    days.forEach((day, dayIndex) => {
      const isLastDay = dayIndex === totalDays - 1;
      const dailyAmount = isLastDay
        ? currency(projection.amount).subtract(allocated).value
        : baseDailyAmount;

      allocated = currency(allocated).add(dailyAmount).value;
      runningValue = Math.max(
        currency(runningValue).subtract(dailyAmount).value,
        0
      );

      dailyRows.push({
        sn: dailyRows.length + 1,
        date: format(day, "dd MMM yyyy"),
        dateObj: day,
        monthKey: format(day, "yyyy-MM"),
        amount: dailyAmount,
        remainingValue: runningValue,
      });
    });
  });

  return dailyRows;
};

const groupProjectionsByMonth = projections => {
  const groups = [];
  const map = new Map();

  projections.forEach(row => {
    const key = row.monthKey;
    if (!map.has(key)) {
      const group = {
        monthKey: key,
        monthLabel: format(row.dateObj, "MMM yyyy"),
        rows: [],
        totalAmount: 0,
        endNBV: 0,
      };
      map.set(key, group);
      groups.push(group);
    }

    const group = map.get(key);
    group.rows.push(row);
    group.totalAmount = currency(group.totalAmount).add(row.amount).value;
    group.endNBV = row.remainingValue;
  });

  return groups;
};

const calculateProjections = ({ item, itemData, startDate, endDate }) => {
  if (!itemData?.depreciation || !item?.UnitCost) {
    return [];
  }

  const {
    percentage = 0,
    cycle,
    Start_Date = new Date(),
  } = itemData.depreciation;

  const openingValue = currency(item.UnitCost)
    // WAS TOLD ASSET MUST BE ONE
    .multiply(/*item.Quantity*/ 1)
    .subtract(itemData?.amountDepreciationBeforeTheStartDate || 0).value;

  const depreciationRate = percentage / 100;
  const startDateObj = new Date(Start_Date);
  const rangeStart = getValidDate(startDate);
  const rangeEnd = getValidDate(endDate);

  if (rangeStart && rangeEnd && startOfDay(rangeStart) > endOfDay(rangeEnd)) {
    return [];
  }

  let projections = [];
  let currentValue = openingValue;
  let serialNumber = 1;
  let previousPeriodEnd = addDays(startDateObj, -1);
  let currentDate = new Date(
    startDateObj.getFullYear(),
    startDateObj.getMonth() + 1,
    0
  );

  let unchangedIterations = 0;

  while (
    currentValue > EPSILON &&
    serialNumber <= MAX_PROJECTION_ITERATIONS_FAILSAFE
  ) {
    let depreciationAmount;

    if (itemData.depreciation.type === "Percentage") {
      depreciationAmount = openingValue * depreciationRate;
    } else {
      depreciationAmount = parseFloat(itemData.depreciation.fixedAmount || 0);
    }

    if (!Number.isFinite(depreciationAmount)) {
      break;
    }

    if (serialNumber === 1) {
      const daysInMonth = getDaysInMonth(startDateObj);
      const daysUsed = daysInMonth - getDate(startDateObj) + 1;

      depreciationAmount = currency(depreciationAmount)
        .multiply(daysUsed)
        .divide(daysInMonth).value;
    }

    if (!Number.isFinite(depreciationAmount)) {
      break;
    }

    depreciationAmount = Math.min(depreciationAmount, currentValue);

    if (depreciationAmount <= EPSILON) {
      break;
    }

    const periodStart =
      serialNumber === 1 ? startDateObj : addDays(previousPeriodEnd, 1);
    const periodEnd = currentDate;

    projections.push({
      sn: serialNumber,
      date: format(currentDate, "dd MMM yyyy"),
      amount: depreciationAmount,
      remainingValue: currentValue - depreciationAmount,
      periodStart,
      periodEnd,
    });

    const nextValue = currentValue - depreciationAmount;

    if (!Number.isFinite(nextValue)) {
      break;
    }

    if (Math.abs(nextValue - currentValue) < 0.000001) {
      unchangedIterations += 1;
      if (unchangedIterations >= 2) {
        break;
      }
    } else {
      unchangedIterations = 0;
    }

    currentValue = nextValue;
    previousPeriodEnd = currentDate;

    switch (cycle) {
      case "Monthly":
        currentDate = addMonths(currentDate, 1);
        currentDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );
        break;
      case "Quaterly": {
        currentDate = addQuarters(currentDate, 1);
        const quarterEndMonth =
          Math.ceil((currentDate.getMonth() + 1) / 3) * 3 - 1;
        currentDate = new Date(
          currentDate.getFullYear(),
          quarterEndMonth + 1,
          0
        );
        break;
      }
      case "Annually":
        currentDate = addYears(currentDate, 1);
        currentDate = new Date(currentDate.getFullYear(), 11, 31);
        break;
      default:
        currentDate = addMonths(currentDate, 1);
        currentDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );
    }

    serialNumber += 1;
  }

  if (!rangeStart && !rangeEnd) {
    return expandPeriodToDailyRows(projections, openingValue);
  }

  let depreciationBeforeRange = 0;

  if (rangeStart) {
    const beforeRangeEnd = addDays(startOfDay(rangeStart), -1);

    projections.forEach(projection => {
      if (startOfDay(projection.periodStart) > beforeRangeEnd) {
        return;
      }

      depreciationBeforeRange += getProratedAmountForRange({
        amount: projection.amount,
        periodStart: projection.periodStart,
        periodEnd: projection.periodEnd,
        rangeStart: null,
        rangeEnd: beforeRangeEnd,
      });
    });
  }

  let runningValue = Math.max(openingValue - depreciationBeforeRange, 0);
  const rangedProjections = [];

  projections.forEach(projection => {
    const rangedAmount = getProratedAmountForRange({
      amount: projection.amount,
      periodStart: projection.periodStart,
      periodEnd: projection.periodEnd,
      rangeStart,
      rangeEnd,
    });

    if (rangedAmount <= 0) {
      return;
    }

    const safeAmount = Math.min(rangedAmount, runningValue);
    runningValue = Math.max(runningValue - safeAmount, 0);

    // Determine effective start/end within the range for daily expansion
    const effectiveStart =
      rangeStart && rangeStart > projection.periodStart
        ? rangeStart
        : projection.periodStart;
    const effectiveEnd =
      rangeEnd && rangeEnd < projection.periodEnd
        ? rangeEnd
        : projection.periodEnd;

    rangedProjections.push({
      sn: rangedProjections.length + 1,
      date: projection.date,
      amount: safeAmount,
      remainingValue: runningValue,
      periodStart: effectiveStart,
      periodEnd: effectiveEnd,
    });
  });

  const rangedOpeningValue = Math.max(
    openingValue - depreciationBeforeRange,
    0
  );
  return expandPeriodToDailyRows(rangedProjections, rangedOpeningValue);
};

// can take the jobnumber to get assets issued to job

// can take list of items with barcodes to get the projection for each item

export default function ProjectedDepreciationTable({
  items = [],
  Bar_Code,
  Warehouse,
  Item_Name,
  UnitCost,
  Quantity,
  jobNumber,
  contracts,

  //
  startDate,
  endDate,
}) {
  const { backendUrl } = useAuth();
  const [openTables, setOpenTables] = useState({});
  const [projectionPages, setProjectionPages] = useState({});
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [groupByMonth, setGroupByMonth] = useState(false);
  const [openMonths, setOpenMonths] = useState({});

  const {
    data: jobIssueItems = [],
    error: jobItemsError,
    refetch: refetchJobItems,
  } = useQuery(
    ["JOB_ISSUE_ITEMS_FOR_DEPRECIATION", jobNumber, contracts],
    async () => {
      const response = await fetchActionsUtil(
        `${backendUrl}/api/warehouse/job-issue-items?&${queryString.stringify({
          page: 1,
          limit: 1000,
          contracts: contracts || jobNumber,
        })}`,
        "GET"
      );

      // only assets can depreciate
      const assetWarehouseIDs = response?.data?.assetWarehouseIDs;

      // only items in asset warehouse
      return (response?.data?.issuedItems || []).filter(item =>
        assetWarehouseIDs.includes(item.Warehouse)
      );
    },
    {
      enabled: Boolean(jobNumber),
    }
  );

  const normalizedItems = useMemo(() => {
    if (jobNumber) {
      return jobIssueItems.map(normalizeItem).filter(item => item.Bar_Code);
    }

    if (Array.isArray(items) && items.length > 0) {
      return items.map(normalizeItem).filter(item => item.Bar_Code);
    }

    if (!Bar_Code) {
      return [];
    }

    return [
      normalizeItem({
        Bar_Code,
        Warehouse,
        Item_Name,
        UnitCost,
        Quantity,
      }),
    ];
  }, [
    Bar_Code,
    Item_Name,
    Quantity,
    UnitCost,
    Warehouse,
    items,
    jobIssueItems,
    jobNumber,
  ]);

  const barCodes = useMemo(
    () =>
      [
        ...new Set(normalizedItems.map(item => item.Bar_Code).filter(Boolean)),
      ].join(","),
    [normalizedItems]
  );

  const getBatches = async () => {
    let response = await fetch(
      `${backendUrl}/api/warehouse/asset-depreciation-projection-multiple-items?${queryString.stringify(
        { Bar_Codes: barCodes }
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();
    return Array.isArray(data) ? data : [];
  };

  const { data = [], error, isFetching, isLoading, refetch } = useQuery(
    ["DEPRECIATION_DATA_MULTIPLE", barCodes],
    getBatches,
    {
      enabled: Boolean(barCodes),
    }
  );

  const activeError = jobItemsError || error;

  const depreciationDataByBarCode = useMemo(() => {
    const responseMap = new Map();

    data.forEach((itemData, index) => {
      const itemBarCode = getResponseBarCode(itemData);

      if (itemBarCode) {
        // we need some data from normalized items to calculate the projection so we set the data with barcode as key and if barcode is not available we set with index as key
        const foundItem = normalizedItems.find(
          item => item.Bar_Code === itemBarCode
        );
        const combinedData = {
          ...itemData,

          // FIELDS FOR CALCULATION
          // WE WAS TOLD ASSET MUST BE ONE
          //  Quantity: foundItem?.Quantity ?? itemData.Quantity ?? 0,
          Quantity: 1,
        };

        responseMap.set(String(itemBarCode), combinedData);
      }

      responseMap.set(`__index_${index}`, itemData);
    });
    console.log("depreciation data by barcode", responseMap);
    return responseMap;
  }, [data]);

  const projectionRows = useMemo(
    () =>
      normalizedItems.map((item, index) => {
        let itemData =
          depreciationDataByBarCode.get(String(item.Bar_Code)) ??
          depreciationDataByBarCode.get(`__index_${index}`) ??
          null;

        // console.log("item data for projection", { item, itemData });

        itemData = {
          ...itemData,
          // we beed to solve in r
          depreciatedValue: 0,
          depreciation: {
            ...itemData?.depreciation.depreciation,
            Start_Date: itemData?.depreciation.Start_Date,
          },
        };

        return {
          item,
          itemData,
          projections: calculateProjections({
            item,
            itemData,

            // we need to see what happened when the item was in the job
            startDate: item.Date_Log, // Date the item was issued
            endDate: new Date(), // Current date (Today)
          }),
        };
      }),
    [depreciationDataByBarCode, normalizedItems, startDate, endDate]
  );

  const totalPeriods = useMemo(
    () => projectionRows.reduce((sum, row) => sum + row.projections.length, 0),
    [projectionRows]
  );

  const exportToExcel = () => {
    const excelData = [
      [
        "Item Code",
        "Item Name",
        "Warehouse",
        "S/N",
        "Date",
        "Month",
        "Amount",
        "Net Book Value",
      ],
      ...projectionRows.flatMap(({ item, projections }) => {
        if (!projections.length) {
          return [
            [item.Bar_Code, item.Item_Name, item.Warehouse, "", "", "", "", ""],
          ];
        }

        return projections.map(projection => [
          item.Bar_Code,
          item.Item_Name,
          item.Warehouse,
          projection.sn,
          projection.date,
          projection.monthKey || "",
          projection.amount,
          projection.remainingValue,
        ]);
      }),
    ];

    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Asset Depreciation");

    const filename = `Projected_Depreciation_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="projected-depreciation-container p-2 p-md-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1">Asset Depreciation</h5>
          {/* <small className="text-muted">
            {projectionRows.length} item(s) | {totalPeriods} period(s)
          </small> */}
        </div>
        <div className="d-flex gap-2">
          <Button
            onClick={() => {
              if (jobNumber) {
                refetchJobItems();
              }
              refetch();
            }}
            variant=""
            className="text-primary"
            disabled={(!barCodes && !jobNumber) || isFetching}
          >
            <CachedIcon />
          </Button>
          <Button
            variant=""
            title="Export"
            className="border"
            onClick={exportToExcel}
            disabled={!projectionRows.length}
          >
            <ExcelIcon color={"green"} /> Export to Excel
          </Button>
        </div>
      </div>

      {!normalizedItems.length ? (
        <NoTableItem />
      ) : (
        <div className="d-flex flex-column gap-2">
          {projectionRows.map(({ item, itemData, projections }, index) => {
            const rowKey = `${item.Bar_Code}-${index}`;
            const isTableOpen = Boolean(openTables[rowKey]);

            const totalDepreciated = projections.reduce(
              (sum, p) => currency(sum).add(p.amount).value,
              0
            );
            const currentValue =
              projections.length > 0
                ? projections[projections.length - 1].remainingValue
                : currency(item.UnitCost || 0).multiply(item.Quantity || 0)
                    .value;

            console.log("rendering row", {
              item,
              itemData,
              projections,
              currentValue,
            });

            return (
              <div key={rowKey} className="bg-white p-0">
                <Table responsive borderless size="sm" className="small mb-2">
                  <tbody>
                    <tr>
                      <th>Item Code</th>
                      <td>{item.Bar_Code}</td>
                      <th>Item Name</th>
                      <td>{item.Item_Name}</td>
                    </tr>
                    <tr>
                      <th>Qty</th>
                      <td>
                        {currency(item.Quantity, {
                          symbol: "",
                          format: formatForQty,
                        }).format()}
                      </td>
                      <th>Cost</th>
                      <td>
                        {currency(item.UnitCost, { symbol: "" }).format()}
                      </td>
                    </tr>
                    <tr>
                      <th>Cycle</th>
                      <td>{itemData?.depreciation?.cycle || "..."}</td>
                      <th>Type</th>
                      <td>{itemData?.depreciation?.type || "..."}</td>
                    </tr>
                    <tr>
                      <th>Post Date</th>
                      <td>
                        {itemData?.depreciation
                          ? formatDate(itemData.depreciation.Post_Date)
                          : "..."}
                      </td>
                      <th>Dpr. Start Date</th>
                      <td>
                        {itemData?.depreciation
                          ? formatDate(itemData.depreciation.Start_Date)
                          : "..."}
                      </td>
                    </tr>

                    <tr>
                      <th>Issued Date</th>
                      <td>
                        {item?.Date_Log ? formatDate(item.Date_Log) : "..."}
                      </td>
                      <th>Issue Time</th>
                      <td>{item?.IssueTime ? item?.IssueTime : "..."}</td>
                    </tr>

                    <tr>
                      <th>Total Unit Cost</th>
                      <td>
                        {currency(item.UnitCost, {
                          symbol: "",
                        })
                          .multiply(item.Quantity)
                          .format()}
                      </td>
                      <th>Total Depreciated</th>
                      <td>
                        {currency(totalDepreciated, {
                          symbol: "",
                        }).format()}
                      </td>
                    </tr>
                    <tr>
                      <th>Current Value</th>
                      <td>
                        {currency(currentValue, {
                          symbol: "",
                        }).format()}
                      </td>
                      <th>Periods</th>
                      <td>{projections.length}</td>
                    </tr>
                    <tr>
                      <th>Projected End</th>
                      <td>
                        {projections.length > 0
                          ? projections[projections.length - 1].date
                          : "N/A"}
                      </td>
                      <th>
                        {itemData?.depreciation?.type === "Percentage"
                          ? "Percentage"
                          : "Fixed Amount"}
                      </th>
                      <td>
                        {itemData?.depreciation?.type === "Percentage"
                          ? `${itemData?.depreciation?.percentage ?? "..."}%`
                          : itemData?.depreciation?.fixedAmount ?? "..."}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {activeError && index === 0 ? (
                  <p className="text-danger small mb-2">
                    {activeError.message}
                  </p>
                ) : null}

                <div className="d-flex justify-content-end mb-2">
                  <div className="d-flex align-items-center gap-2 me-2">
                    <small className="text-muted">Rows:</small>
                    <select
                      className="form-select form-select-sm"
                      value={rowsPerPage}
                      onChange={e => {
                        const nextRows = Number(e.target.value);
                        setRowsPerPage(nextRows);
                        setProjectionPages({});
                      }}
                      style={{ width: 84 }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <Button
                    size="sm"
                    variant={groupByMonth ? "primary" : "outline-primary"}
                    className="me-2"
                    onClick={() => {
                      setGroupByMonth(prev => !prev);
                      setOpenMonths({});
                      setProjectionPages({});
                    }}
                  >
                    {groupByMonth ? "Show Daily" : "Group by Month"}
                  </Button>
                  <Button
                    size="sm"
                    variant="light-blue"
                    onClick={() =>
                      setOpenTables(prev => ({
                        ...prev,
                        [rowKey]: !prev[rowKey],
                      }))
                    }
                  >
                    {isTableOpen ? "Hide Projections" : "Show Projections"}
                  </Button>
                </div>

                <Collapse in={isTableOpen}>
                  <div>
                    {isLoading && !itemData ? (
                      <div className="small text-muted">Loading...</div>
                    ) : projections.length > 0 ? (
                      (() => {
                        if (groupByMonth) {
                          const monthGroups = groupProjectionsByMonth(
                            projections
                          );
                          const totalPages = Math.max(
                            1,
                            Math.ceil(monthGroups.length / rowsPerPage)
                          );
                          const currentPage = Math.min(
                            projectionPages[rowKey] || 1,
                            totalPages
                          );
                          const startIndex = (currentPage - 1) * rowsPerPage;
                          const paginatedGroups = monthGroups.slice(
                            startIndex,
                            startIndex + rowsPerPage
                          );

                          return (
                            <>
                              <Table
                                responsive
                                bordered
                                size="sm"
                                className="mb-2"
                              >
                                <thead>
                                  <tr>
                                    <th style={{ width: 40 }} />
                                    <th>Month</th>
                                    <th>Total Depreciation</th>
                                    <th>Net Book Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paginatedGroups.map(group => {
                                    const monthOpenKey = `${rowKey}__${
                                      group.monthKey
                                    }`;
                                    const isMonthOpen = Boolean(
                                      openMonths[monthOpenKey]
                                    );

                                    return (
                                      <Fragment key={group.monthKey}>
                                        <tr
                                          onClick={() =>
                                            setOpenMonths(prev => ({
                                              ...prev,
                                              [monthOpenKey]: !prev[
                                                monthOpenKey
                                              ],
                                            }))
                                          }
                                          style={{
                                            cursor: "pointer",
                                            backgroundColor: "#f0f4ff",
                                            fontWeight: 600,
                                          }}
                                        >
                                          <td>
                                            {isMonthOpen ? (
                                              <ChevronDownIcon size={16} />
                                            ) : (
                                              <ChevronRightIcon size={16} />
                                            )}
                                          </td>
                                          <td>{group.monthLabel}</td>
                                          <td>
                                            {currency(group.totalAmount, {
                                              symbol: "",
                                            }).format()}
                                          </td>
                                          <td>
                                            {currency(group.endNBV, {
                                              symbol: "",
                                            }).format()}
                                          </td>
                                        </tr>
                                        {isMonthOpen
                                          ? group.rows.map(row => (
                                              <tr
                                                key={`${group.monthKey}-${
                                                  row.sn
                                                }`}
                                                className="small"
                                              >
                                                <td />
                                                <td>{row.date}</td>
                                                <td>
                                                  {currency(row.amount, {
                                                    symbol: "",
                                                  }).format()}
                                                </td>
                                                <td>
                                                  {currency(
                                                    row.remainingValue,
                                                    { symbol: "" }
                                                  ).format()}
                                                </td>
                                              </tr>
                                            ))
                                          : null}
                                      </Fragment>
                                    );
                                  })}
                                </tbody>
                              </Table>

                              {totalPages > 1 ? (
                                <div className="d-flex justify-content-between align-items-center small">
                                  <span className="text-muted">
                                    Page {currentPage} of {totalPages} (
                                    {monthGroups.length} months)
                                  </span>
                                  <div className="d-flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      disabled={currentPage <= 1}
                                      onClick={() =>
                                        setProjectionPages(prev => ({
                                          ...prev,
                                          [rowKey]: Math.max(
                                            1,
                                            (prev[rowKey] || 1) - 1
                                          ),
                                        }))
                                      }
                                    >
                                      Previous
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      disabled={currentPage >= totalPages}
                                      onClick={() =>
                                        setProjectionPages(prev => ({
                                          ...prev,
                                          [rowKey]: Math.min(
                                            totalPages,
                                            (prev[rowKey] || 1) + 1
                                          ),
                                        }))
                                      }
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              ) : null}
                            </>
                          );
                        }

                        // Daily flat view (default)
                        const totalPages = Math.max(
                          1,
                          Math.ceil(projections.length / rowsPerPage)
                        );
                        const currentPage = Math.min(
                          projectionPages[rowKey] || 1,
                          totalPages
                        );
                        const startIndex = (currentPage - 1) * rowsPerPage;
                        const paginatedProjections = projections.slice(
                          startIndex,
                          startIndex + rowsPerPage
                        );

                        return (
                          <>
                            <Table
                              responsive
                              bordered
                              striped
                              size="sm"
                              className="mb-2"
                            >
                              <thead>
                                <tr>
                                  <th>S/N</th>
                                  <th>Date</th>
                                  <th>Amount</th>
                                  <th>Net Book Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {paginatedProjections.map(projection => (
                                  <tr key={projection.sn}>
                                    <td>{projection.sn}</td>
                                    <td>{projection.date}</td>
                                    <td>
                                      {currency(projection.amount, {
                                        symbol: "",
                                      }).format()}
                                    </td>
                                    <td>
                                      {currency(projection.remainingValue, {
                                        symbol: "",
                                      }).format()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>

                            {totalPages > 1 ? (
                              <div className="d-flex justify-content-between align-items-center small">
                                <span className="text-muted">
                                  Page {currentPage} of {totalPages}
                                </span>
                                <div className="d-flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={currentPage <= 1}
                                    onClick={() =>
                                      setProjectionPages(prev => ({
                                        ...prev,
                                        [rowKey]: Math.max(
                                          1,
                                          (prev[rowKey] || 1) - 1
                                        ),
                                      }))
                                    }
                                  >
                                    Previous
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={currentPage >= totalPages}
                                    onClick={() =>
                                      setProjectionPages(prev => ({
                                        ...prev,
                                        [rowKey]: Math.min(
                                          totalPages,
                                          (prev[rowKey] || 1) + 1
                                        ),
                                      }))
                                    }
                                  >
                                    Next
                                  </Button>
                                </div>
                              </div>
                            ) : null}
                          </>
                        );
                      })()
                    ) : (
                      <NoTableItem />
                    )}
                  </div>
                </Collapse>

                <hr className="my-5" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
